import { Bot, Receipt, ShoppingBag, TrendingUp } from "lucide-react";
import { useEffect, useState } from "react";

import { CarregandoCards } from "../../components/carregando";

const API_URL = process.env.REACT_APP_BACKEND_URL || "";
const PERIODOS = [{ dias: 7, rotulo: "7 dias" }, { dias: 30, rotulo: "30 dias" }, { dias: 90, rotulo: "90 dias" }];
const reais = (v) => (Number(v) || 0).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });

/**
 * Vendas da CLEO (ChatBot → WhatsApp): pedidos que ela anotou pela conversa,
 * faturamento e ticket médio no período, mais o caminho das conversas em que
 * o cliente começou a pedir. Lê /api/chatbot/vendas-cleo. Cancelado não conta.
 */
export default function VendasCleoCard() {
  const [dias, setDias] = useState(30);
  const [dados, setDados] = useState(null);

  useEffect(() => {
    let vivo = true;
    setDados(null);
    fetch(`${API_URL}/api/chatbot/vendas-cleo?dias=${dias}`, {
      headers: { Authorization: `Bearer ${localStorage.getItem("token") || ""}` }, cache: "no-store",
    })
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => { if (vivo) setDados(d || { erro: true }); })
      .catch(() => { if (vivo) setDados({ erro: true }); });
    return () => { vivo = false; };
  }, [dias]);

  const funil = dados?.funil || {};
  const ofertas = dados?.ofertas || {};
  return (
    <div className="mt-6" data-e2e="card-vendas-cleo">
      <div className="mb-2 flex items-center justify-between gap-2">
        <p className="flex items-center gap-1.5 text-sm font-semibold"><Bot className="h-4 w-4 text-emerald-500" /> Vendas da CLEO</p>
        <div className="flex gap-1">
          {PERIODOS.map((p) => (
            <button key={p.dias} type="button" onClick={() => setDias(p.dias)} data-e2e={`vendas-cleo-${p.dias}`}
              className={`rounded-md px-2 py-1 text-xs ${dias === p.dias ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground hover:bg-muted/70"}`}>
              {p.rotulo}
            </button>
          ))}
        </div>
      </div>
      <p className="mb-2 text-xs text-muted-foreground">Pedidos que a CLEO anotou pela conversa do WhatsApp. Cancelado não conta.</p>
      {dados === null ? (
        // Os três quadros (pedidos, faturamento, ticket) em cinza, no lugar de "Carregando…"
        <CarregandoCards quantos={3} className="grid grid-cols-3 gap-2 sm:gap-3" altura="h-[92px]" />
      ) : dados.erro ? (
        <p className="text-sm text-muted-foreground">Não foi possível carregar agora.</p>
      ) : (
        <>
          <div className="grid grid-cols-3 gap-2 sm:gap-3">
            <div className="rounded-lg border bg-white p-2 text-center dark:bg-gray-800 sm:p-3">
              <ShoppingBag className="mx-auto mb-1 h-4 w-4 text-emerald-500" />
              <p className="text-lg font-bold leading-tight text-emerald-600 sm:text-2xl" data-e2e="vendas-cleo-pedidos">{(dados.pedidos || 0).toLocaleString("pt-BR")}</p>
              <p className="text-[10px] text-muted-foreground">Pedidos anotados</p>
            </div>
            <div className="rounded-lg border bg-white p-2 text-center dark:bg-gray-800 sm:p-3">
              <TrendingUp className="mx-auto mb-1 h-4 w-4 text-blue-500" />
              <p className="text-lg font-bold leading-tight text-blue-600 sm:text-2xl" data-e2e="vendas-cleo-faturamento">{reais(dados.faturamento)}</p>
              <p className="text-[10px] text-muted-foreground">Faturamento</p>
            </div>
            <div className="rounded-lg border bg-white p-2 text-center dark:bg-gray-800 sm:p-3">
              <Receipt className="mx-auto mb-1 h-4 w-4 text-purple-500" />
              <p className="text-lg font-bold leading-tight text-purple-600 sm:text-2xl">{reais(dados.ticket_medio)}</p>
              <p className="text-[10px] text-muted-foreground">Ticket médio</p>
            </div>
          </div>
          {funil.comecaram_a_pedir > 0 ? (
            <p className="mt-2 text-xs text-muted-foreground" data-e2e="vendas-cleo-funil">
              De <b>{funil.comecaram_a_pedir}</b> conversas em que o cliente começou a pedir, <b>{funil.viraram_pedido}</b> viraram
              pedido ({String(funil.conversao_pct).replace(".", ",")}%), {funil.desistiram} desistiram, {funil.pararam_no_meio} pararam no meio{funil.fecharam_pelo_cardapio > 0 ? `, ${funil.fecharam_pelo_cardapio} fecharam pelo cardápio` : ""}
              {funil.foram_pro_atendente > 0 ? ` e ${funil.foram_pro_atendente} foram pro atendente` : ""}.
            </p>
          ) : (
            <p className="mt-2 text-xs text-muted-foreground">
              Ainda não há pedido anotado pela CLEO neste período. A chave fica em Sistema → Preferências → Atendimento.
            </p>
          )}
          {/* "Forçar a venda": o que a CLEO ofereceu a mais no meio do pedido e o que isso trouxe */}
          {ofertas.feitas > 0 && (
            <p className="mt-1 text-xs text-muted-foreground" data-e2e="vendas-cleo-ofertas">
              Ela ofereceu um item a mais (combo, borda, acompanhamento) em <b>{ofertas.feitas}</b> conversas e <b>{ofertas.aceitas}</b> aceitaram
              ({String(ofertas.aceite_pct).replace(".", ",")}%).
              {ofertas.pedidos_com_oferta > 0 ? ` Isso somou ${reais(ofertas.receita_a_mais)} aos pedidos. Ticket médio com oferta aceita: ${reais(ofertas.ticket_com_oferta)}; sem: ${reais(ofertas.ticket_sem_oferta)}.` : ""}
            </p>
          )}
        </>
      )}
    </div>
  );
}
